import Footer from '@react/shared/components/Footer';
import Image from '@react/shared/components/Image';
import { ReviewerBody } from '@react/shared/types/usePrefetch';
import { RESOURCE_CENTER_PATH } from '@sly/core/constants/dashboardAppPaths';
import { populate } from '@sly/core/constants/populate';
import cx from 'classnames';
import React, { useEffect } from 'react';
import { assetPath } from 'sly/lib/path';
import Header from 'sly/resourceCenter/components/Header';
import Helmet from 'sly/resourceCenter/components/Helmet';
import NavPaths from 'sly/resourceCenter/components/NavPaths';
import ResourceCenterChatBotWrapper from 'sly/resourceCenter/components/ResourceCenterChatBotWrapper';
import { usePrefetch } from 'sly/services/api/prefetch';
import { pageNames, useEvents } from 'sly/services/events';

import ReviewerCard from './reviewerCard';
import { pagePadding } from '../../styles';

interface GroupedReviewers {
  [key: string]: ReviewerBody[];
}

const groupBy = function (xs: ReviewerBody[]) {
  return xs.reduce((rv: GroupedReviewers, x) => {
    (rv[x.category] = rv[x.category] || []).push(x);
    return rv;
  }, {});
};

const ReviewerIndexPage = () => {
  const {
    requestInfo: { result, hasFinished },
  } = usePrefetch('getReviewer', {
    populate: populate.reviewers,
    'filters[status]': 'active',
    sort: 'order',
  });
  const reviewers = result?.data || [];

  const { events } = useEvents();
  useEffect(() => {
    events.page(pageNames.Reviewers, {});
  }, []);

  if (!hasFinished) {
    return (
      <div
        className='flex h-[100vh] items-center justify-center'
        data-tp-id='gen-div-8e8d1543-12a1-4b8d-92c8-f30a8fa49c46'
      >
        <Image
          src={assetPath('uploads/react-assets/homebase/loader.svg')}
          layout='fill'
          alt='loader'
          data-tp-id='gen-Image-e6163b37-e64f-41e9-8bec-88371836f0b0'
        />
      </div>
    );
  }

  const paths = [
    {
      name: 'Resource Center Home',
      to: RESOURCE_CENTER_PATH,
    },
    {
      name: 'Reviewers',
      to: null,
    },
  ];

  const groupedReviewers: GroupedReviewers | Record<string, never> = reviewers
    ? groupBy(reviewers)
    : {};
  const categories = ['seniorly'];
  categories.push(
    ...Object.keys(groupedReviewers).filter((key) => key !== 'seniorly')
  );

  return (
    <>
      <ResourceCenterChatBotWrapper>
        <Helmet
          path={`${RESOURCE_CENTER_PATH}/reviewers`}
          title='Reviewers | Seniorly'
          shortDescription="Learn about Seniorly's content reviewers who ensure accuracy and quality in our senior living articles. Discover their commitment to delivering reliable information to seniors and their families"
        />

        <Header />

        <div
          className='bg-viridian-lighter-90'
          data-tp-id='gen-div-dfcbf71d-24be-4ad6-a443-e74deecc91dc'
        >
          <div
            className='mx-6 py-xxl px-0 md:py-xxxl lg:mx-[240px]'
            data-tp-id='gen-div-7519230b-f63f-4aef-af05-f5843ca00d46'
          >
            <div className='md:mx-auto'>
              <NavPaths paths={paths} />
              <h1
                className='font-t-xl pt-m'
                data-tp-id='gen-div-296ba96f-4938-42b7-9695-0c501dadf384'
              >
                Meet our reviewers
              </h1>
              <div
                className='font-b-m my-6 mx-0 max-w-[680px]'
                data-tp-id='gen-div-f15ab12e-847b-44ef-8e8f-7c5c4911a451'
              >
                Seniorly works with specialists in aging and senior living who
                have given their time to review our articles for accuracy and
                relevance. Seniorly reviewers range from Professors, Financial
                Advisors, Gerontologists, Psychologists and more.
              </div>
            </div>
          </div>
        </div>
        <hr />

        <div
          className={cx(pagePadding, 'flex flex-col justify-center')}
          data-tp-id='gen-div-1a2208cb-4853-488c-af40-bf78f8a723e1'
        >
          <>
            {categories &&
              categories.map((category) => (
                <>
                  {category !== 'seniorly' &&
                    category !== 'undefined' &&
                    category !== 'null' && (
                      <>
                        <hr />
                        <div
                          className='font-t-l my-xxl mx-0 text-center capitalize md:my-xxxl'
                          data-tp-id='gen-div-e9d4efb2-4737-46d8-9f64-71eb1eef929e'
                        >
                          {category} contributors
                        </div>
                        <hr />
                      </>
                    )}
                  {groupedReviewers[category] &&
                    groupedReviewers[category].map((reviewer, index) => (
                      <>
                        <div
                          className='my-xxl mx-0 md:my-xxxl lg:mx-[216px]'
                          data-tp-id='gen-div-476844f7-763c-4f04-b6f6-7a751bdfbee0'
                        >
                          <ReviewerCard reviewer={reviewer} />
                        </div>
                        {index < groupedReviewers[category].length - 1 ? (
                          <hr />
                        ) : (
                          ''
                        )}
                      </>
                    ))}
                </>
              ))}
          </>
        </div>
        <Footer />
      </ResourceCenterChatBotWrapper>
    </>
  );
};

ReviewerIndexPage.displayName = 'ReviewerIndex';

export default ReviewerIndexPage;
